import {
  FireOutlined,
  LikeFilled,
  LikeOutlined,
  RadarChartOutlined,
  SearchOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import { Drawer, Input, Spin } from "antd";
import dayjs from "dayjs";
import { EasyAvatar } from "fin-web-components";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { H5SubPageShell } from "../../components/common/H5SubPageShell";
import "../../components/common/H5SubPageShell.css";
import type { IntelCommentKind } from "../../space/SpaceType";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { formatEngagementCount } from "../../utils/formatEngagementCount";
import { h5NavigateTo } from "../../utils/miniProgramEnv";
import { useMainScrollPullRefresh } from "../../utils/useMainScrollPullRefresh";
import {
  intelligenceCardLevel,
  intelligenceImportanceLabel,
  intelligenceNewsTypeLabel,
  INTELLIGENCE_NEWS_TYPE_OPTIONS,
  INTELLIGENCE_POLL_MS,
} from "./Config";
import { markIntelLastVisit } from "./IntelLastVisitLocalStore";
import { IntelligenceFeedModel } from "./IntelligenceFeedModel";
import type {
  IntelligenceFeedItem,
  IntelligenceStockPick,
} from "./IntelligenceFeedType";
import "./IntelligenceFeedPage.css";

function displayTime(value: string | Date): string {
  const date = dayjs(value);
  return date.isValid() ? date.format("M月D日 HH:mm") : String(value);
}

function IntelligenceOccurredAt({ value }: { value: string | Date }) {
  const date = dayjs(value);
  if (!date.isValid()) return <span>{String(value)}</span>;
  return (
    <span className="lf-h5-intel-card__occurred-at">
      <span>{date.format("M月D日")}</span>
      <span>{date.format("HH:mm")}</span>
    </span>
  );
}

function pullRefreshHint(phase: string): string {
  if (phase === "refreshing") return "刷新中…";
  if (phase === "ready") return "松开刷新";
  if (phase === "pulling") return "下拉刷新";
  return "";
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** 按已生效关键词高亮纯文本 */
function highlightKeyword(text: string, keyword: string): React.ReactNode {
  const source = text ?? "";
  const kw = keyword.trim();
  if (!source || !kw) {
    return source;
  }
  const parts = source.split(new RegExp(`(${escapeRegExp(kw)})`, "gi"));
  if (parts.length === 1) {
    return source;
  }
  return parts.map((part, index) =>
    part.toLowerCase() === kw.toLowerCase() ? (
      <mark key={`${part}-${index}`} className="lf-h5-intel-highlight">
        {part}
      </mark>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );
}

function IntelligenceHeat({ value }: { value: number }) {
  const filled = Math.max(1, Math.min(5, Math.ceil(value / 2)));
  return (
    <span className="lf-h5-intel-strength" aria-label={`情报热度 ${value} 分`}>
      <span className="lf-h5-intel-strength__label">热度</span>
      <span className="lf-h5-intel-strength__bars" aria-hidden>
        {Array.from({ length: 5 }, (_, index) => (
          <i
            key={index}
            className={index < filled ? "is-filled" : ""}
          />
        ))}
      </span>
    </span>
  );
}

function EngagementActions({
  item,
  model,
}: {
  item: IntelligenceFeedItem;
  model: IntelligenceFeedModel;
}) {
  return (
    <div className="lf-h5-intel-actions">
      <button
        type="button"
        aria-label={item.likedByMe ? "取消点赞" : "点赞"}
        onClick={(event) => {
          event.stopPropagation();
          void model.toggleLike(item);
        }}
      >
        {item.likedByMe ? <LikeFilled className="is-liked" /> : <LikeOutlined />}
        <span>{formatEngagementCount(item.likeCount)}</span>
      </button>
      <button
        type="button"
        aria-label={item.collectedByMe ? "取消收藏" : "收藏"}
        onClick={(event) => {
          event.stopPropagation();
          void model.toggleCollect(item);
        }}
      >
        {item.collectedByMe ? (
          <StarFilled className="is-collected" />
        ) : (
          <StarOutlined />
        )}
        <span>{formatEngagementCount(item.collectCount)}</span>
      </button>
    </div>
  );
}

function StockInsightCard({
  pick,
  spaceId,
}: {
  pick: IntelligenceStockPick;
  spaceId: string;
}) {
  const navigate = useNavigate();
  const change = Number.parseFloat(pick.changePct);
  const changeClass =
    Number.isFinite(change) && change < 0
      ? "is-down"
      : Number.isFinite(change) && change > 0
        ? "is-up"
        : "";
  return (
    <button
      type="button"
      className="lf-h5-intel-stock"
      onClick={() =>
        h5NavigateTo(
          navigate,
          `/space/${spaceId}/stock/${encodeURIComponent(pick.code.trim())}`
        )
      }
    >
      <span className="lf-h5-intel-stock__head">
        <span>
          <span className="lf-h5-intel-stock__name">{pick.name}</span>
          <span className="lf-h5-intel-stock__code">{pick.code}</span>
        </span>
        <span className={`lf-h5-intel-stock__quote ${changeClass}`}>
          {pick.lastPrice || "—"} {pick.changePct || ""}
        </span>
      </span>
      <span className="lf-h5-intel-stock__reason">{pick.recommendReason}</span>
      <span className="lf-h5-intel-stock__scores">
        <span>基本面 {pick.fundamentalScore ?? "—"}</span>
        <span>位置 {pick.positionScore ?? "—"}</span>
      </span>
    </button>
  );
}

function DetailContent({
  item,
  commentKind,
  spaceId,
}: {
  item: IntelligenceFeedItem;
  commentKind: IntelCommentKind;
  spaceId: string;
}) {
  if (commentKind === "manual_comment") {
    if (!item.hasTeacherComment) {
      return <div className="lf-h5-intel-detail__empty">老师暂未点评，请稍后查看</div>;
    }
    const teacherName =
      item.teacher?.nickname?.trim() ||
      item.teacher?.realName?.trim() ||
      "老师";
    return (
      <section className="lf-h5-intel-teacher">
        <div className="lf-h5-intel-teacher__head">
          <EasyAvatar
            shape="square"
            squareRadius={4}
            size={40}
            url={item.teacher?.avatarUrl?.trim() || undefined}
            word={avatarDisplayWord(teacherName)}
            alt=""
          />
          <div>
            <div className="lf-h5-intel-teacher__name">{teacherName}</div>
            <div className="lf-h5-intel-teacher__label">专业点评</div>
          </div>
        </div>
        <div className="lf-h5-intel-teacher__body">{item.teacherComment}</div>
      </section>
    );
  }

  if (commentKind !== "ai_recommend") return null;

  return (
    <section className="lf-h5-intel-insight">
      <div className="lf-h5-intel-disclaimer">
        AI 根据规则生成，仅供参考，不构成投资建议。
      </div>
      {item.extraKind === "ai_risk" ? (
        <div className="lf-h5-intel-risk">
          <div className="lf-h5-intel-detail__section-title">风险提示</div>
          <div>{item.riskDetail || "暂无补充说明"}</div>
        </div>
      ) : (
        <>
          <div className="lf-h5-intel-detail__section-title">关联标的</div>
          <div className="lf-h5-intel-stock-list">
            {item.stocks.length > 0 ? (
              item.stocks.map((pick) => (
                <StockInsightCard
                  key={`${item.id}-${pick.code}`}
                  pick={pick}
                  spaceId={spaceId}
                />
              ))
            ) : (
              <div className="lf-h5-intel-detail__empty">暂无关联标的</div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

export interface IntelligenceFeedPageProps {
  spaceId: string;
  spaceProductId: string;
  displayName: string;
  spaceDisplayName: string;
  intelCommentKind: IntelCommentKind;
}

const IntelligenceFeedPage: React.FC<IntelligenceFeedPageProps> = observer(
  ({ spaceId, spaceProductId, displayName, spaceDisplayName, intelCommentKind }) => {
    const model = useMemo(
      () => new IntelligenceFeedModel(spaceId, spaceProductId),
      [spaceId, spaceProductId]
    );
    const [detailId, setDetailId] = useState<string | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const selectedItem =
      model.items.find((item) => item.id === detailId) ?? null;

    useEffect(() => {
      markIntelLastVisit(spaceId, spaceProductId);
    }, [spaceId, spaceProductId]);

    useEffect(() => {
      void model.loadFirst();
    }, [model]);

    useEffect(() => {
      const timer = window.setInterval(() => {
        void model.syncVisible();
      }, INTELLIGENCE_POLL_MS);
      return () => window.clearInterval(timer);
    }, [model]);

    useEffect(() => {
      const sentinel = sentinelRef.current;
      if (!sentinel) return undefined;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) void model.loadMore();
        },
        { rootMargin: "160px 0px" }
      );
      observer.observe(sentinel);
      return () => observer.disconnect();
    }, [model]);

    const refresh = useCallback(async () => {
      await model.loadFirst();
    }, [model]);
    const pullRefresh = useMainScrollPullRefresh(
      refresh,
      true,
      ".lf-h5-intel__body"
    );
    const ptrVisible =
      pullRefresh.pullDistance > 0 || pullRefresh.phase === "refreshing";

    const openLatest = async () => {
      await model.viewLatest();
      document
        .querySelector<HTMLElement>(".lf-h5-intel__body")
        ?.scrollTo({ top: 0, behavior: "smooth" });
    };

    const latestButton = (
      <button
        type="button"
        className="lf-h5-intel__latest"
        onClick={() => void openLatest()}
      >
        查看最新
      </button>
    );

    return (
      <div className="lf-h5-intel-page-shell">
        <H5SubPageShell
          title={displayName}
          backTo={`/space/${spaceId}/home`}
          headerRight={latestButton}
          shellClassName="lf-h5-intel-sub-page"
        >
          <div className="lf-h5-intel">
            <header className="lf-h5-intel__header" data-pull-refresh-ignore>
              <div className="lf-h5-intel__search">
                <Input
                  allowClear
                  value={model.draftKeyword}
                  placeholder="搜索标题或内容"
                  prefix={<SearchOutlined />}
                  onChange={(event) => {
                    const next = event.target.value;
                    const hadKeyword = Boolean(model.appliedKeyword);
                    model.setDraftKeyword(next);
                    if (!next.trim() && hadKeyword) {
                      void model.clearKeyword();
                    }
                  }}
                  onPressEnter={() => void model.applyKeyword()}
                />
                <button type="button" onClick={() => void model.applyKeyword()}>
                  搜索
                </button>
              </div>
              <div
                className="lf-h5-intel__filters"
                role="radiogroup"
                aria-label="情报筛选"
              >
                <button
                  type="button"
                  className={`lf-h5-intel__filter-fixed${
                    model.scope === "all" ? " is-active" : ""
                  }`}
                  onClick={() => void model.selectScope("all")}
                >
                  全部
                </button>
                <div className="lf-h5-intel__filters-scroll-wrap">
                  <div className="lf-h5-intel__filters-scroll">
                    {(
                      [
                        ["liked", "点赞"],
                        ["collected", "收藏"],
                      ] as const
                    ).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        className={model.scope === value ? "is-active" : ""}
                        onClick={() => void model.selectScope(value)}
                      >
                        {label}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={model.importantOnly ? "is-active" : ""}
                      onClick={() => void model.toggleImportantOnly()}
                    >
                      重要
                    </button>
                    {INTELLIGENCE_NEWS_TYPE_OPTIONS.map((option) => (
                      <button
                        key={option.type}
                        type="button"
                        className={
                          model.newsType === option.type ? "is-active" : ""
                        }
                        onClick={() => void model.selectNewsType(option.type)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </header>

            <div className="lf-h5-intel__body">
              <div
                className={`lf-h5-intel__pull${ptrVisible ? " is-visible" : ""}`}
                style={{ height: ptrVisible ? pullRefresh.pullDistance : 0 }}
                aria-hidden={!ptrVisible}
              >
                <span>
                  {pullRefresh.phase === "refreshing" ? (
                    <>
                      <Spin size="small" /> {pullRefreshHint(pullRefresh.phase)}
                    </>
                  ) : (
                    pullRefreshHint(pullRefresh.phase)
                  )}
                </span>
              </div>

              <div className="lf-h5-intel__status-row">
                <div className="lf-h5-intel__live">
                  <span className="lf-h5-intel__live-dot" />
                  <span>7×24 小时情报更新中</span>
                </div>
                {model.hasActiveFilter ? (
                  <button
                    type="button"
                    className="lf-h5-intel__clear"
                    onClick={() => void model.clearFilters()}
                  >
                    清空筛选
                  </button>
                ) : null}
              </div>

              <Spin
                spinning={
                  model.loading && pullRefresh.phase !== "refreshing"
                }
              >
                <main className="lf-h5-intel__list">
                  {model.items.map((item) => {
                    const secondary =
                      intelCommentKind === "manual_comment"
                        ? item.hasTeacherComment
                          ? "老师已点评"
                          : "等待点评"
                        : item.extraKind === "ai_risk"
                          ? "风险提示"
                          : item.stocks.length > 0
                            ? `${item.stocks.length} 个关联标的`
                            : "";
                    return (
                      <article
                        key={item.id}
                        className={`lf-h5-intel-card lf-h5-intel-card--level-${intelligenceCardLevel(
                          item.importance
                        )}`}
                        onClick={() => setDetailId(item.id)}
                      >
                        <div className="lf-h5-intel-card__body">
                          <div className="lf-h5-intel-card__meta">
                            <span className="lf-h5-intel-card__type">
                              {intelligenceNewsTypeLabel(item.type)}
                            </span>
                            <IntelligenceOccurredAt value={item.occurredAt} />
                          </div>
                          <h2>
                            {highlightKeyword(item.title, model.appliedKeyword)}
                          </h2>
                          <p>
                            {highlightKeyword(
                              item.summary,
                              model.appliedKeyword
                            )}
                          </p>
                          {secondary ? (
                            <div className="lf-h5-intel-card__insight">
                              <RadarChartOutlined />
                              <span>{secondary}</span>
                            </div>
                          ) : null}
                          <footer>
                            <div className="lf-h5-intel-card__heat-row">
                              <IntelligenceHeat value={item.importance} />
                              {intelligenceImportanceLabel(item.importance) ? (
                                <span className="lf-h5-intel-card__level">
                                  {intelligenceImportanceLabel(item.importance)}
                                </span>
                              ) : null}
                            </div>
                            <EngagementActions item={item} model={model} />
                          </footer>
                        </div>
                      </article>
                    );
                  })}
                  {!model.loading && model.items.length === 0 ? (
                    <div className="lf-h5-intel__empty">
                      <FireOutlined />
                      <span>
                        {model.loadError || "暂无符合条件的情报"}
                      </span>
                    </div>
                  ) : null}
                  <div ref={sentinelRef} className="lf-h5-intel__sentinel" />
                  {model.loadingMore ? (
                    <div className="lf-h5-intel__more">
                      <Spin size="small" /> 加载中…
                    </div>
                  ) : null}
                  {!model.loading &&
                  model.items.length > 0 &&
                  !model.hasMore ? (
                    <div className="lf-h5-intel__end">已加载全部情报</div>
                  ) : null}
                </main>
              </Spin>
            </div>

            <Drawer
              className="lf-h5-intel-detail"
              placement="bottom"
              height="78vh"
              open={selectedItem != null}
              onClose={() => setDetailId(null)}
              title={
                selectedItem
                  ? intelligenceNewsTypeLabel(selectedItem.type)
                  : ""
              }
              closable={{ placement: "end" }}
              destroyOnClose
            >
              {selectedItem ? (
                <div className="lf-h5-intel-detail__content">
                  <div className="lf-h5-intel-detail__time">
                    {displayTime(selectedItem.occurredAt)}
                  </div>
                  <h2>
                    {highlightKeyword(
                      selectedItem.title,
                      model.appliedKeyword
                    )}
                  </h2>
                  <p className="lf-h5-intel-detail__summary">
                    {highlightKeyword(
                      selectedItem.summary,
                      model.appliedKeyword
                    )}
                  </p>
                  <div className="lf-h5-intel-detail__engagement">
                    <IntelligenceHeat value={selectedItem.importance} />
                    <EngagementActions item={selectedItem} model={model} />
                  </div>
                  <DetailContent
                    item={selectedItem}
                    commentKind={intelCommentKind}
                    spaceId={spaceId}
                  />
                  <div className="lf-h5-intel-detail__brand">
                    龙粉社ID：
                    <span className="lf-h5-intel-detail__brand-id">{spaceId}</span>
                    {" · "}
                    {spaceDisplayName}
                  </div>
                </div>
              ) : null}
            </Drawer>
          </div>
        </H5SubPageShell>
      </div>
    );
  }
);

export default IntelligenceFeedPage;
